import { createSelector, createSlice } from "@reduxjs/toolkit";

const initialState = {
  dishProductsForModalArray: [],
  selectedDishProduct: null,
  dishProductsWeight: 0,
  dishProductsWeightNetto: 0,
  dishProductsCost: 0,
  dishProductEnergy: 0,
  dishProductsFats: 0,
  dishProductsProteins: 0,
  dishProductsCarbohydrates: 0,
  trigger: 0,
};

const dishProductsSlice = createSlice({
  name: "dishProductsSlice",
  initialState,
  reducers: {
    changeTrigger: (state) => {
      state.trigger += 1;
    },
    updateArray: (state, action) => {
      state.dishProductsForModalArray = action.payload.map((product) => {
        // If nutrients are already calculated from API (product.product has calculated values)
        // use them directly instead of recalculating
        if (product.product && product.product.energy !== undefined) {
          // API data - nutrients are already calculated for the specific weight
          return {
            ...product,
            energy: Number(product.product.energy || 0),
            fats: Number(product.product.fats || 0),
            proteins: Number(product.product.proteins || 0),
            carbohydrates: Number(product.product.carbohydrates || 0),
          };
        } else {
          // Manual entry - calculate nutrients based on weight
          return {
            ...product,
            energy: Number(
              (
                ((product.product?.energy ?? product.energy) * product.weight) /
                100
              ).toFixed(2)
            ),
            fats: Number(
              (
                ((product.product?.fats ?? product.fats) * product.weight) /
                100
              ).toFixed(2)
            ),
            proteins: Number(
              (
                ((product.product?.proteins ?? product.proteins) *
                  product.weight) /
                100
              ).toFixed(2)
            ),
            carbohydrates: Number(
              (
                ((product.product?.carbohydrates ?? product.carbohydrates) *
                  product.weight) /
                100
              ).toFixed(2)
            ),
          };
        }
      });
      dishProductsSlice.caseReducers.recalculateTotals(state);
    },
    getDishProductsForModal: (state, action) => {
      state.selectedDishProduct = state.dishProductsForModalArray.find(
        (product) => product.id === action.payload.id
      );
    },
    addDishProductsForModal: (state, action) => {
      state.dishProductsForModalArray.push(action.payload);
      dishProductsSlice.caseReducers.recalculateTotals(state);
    },
    updateDishProductsForModal: (state, action) => {
      state.dishProductsForModalArray = action.payload;
      dishProductsSlice.caseReducers.recalculateTotals(state);
    },
    // updateDishProductsForModal: (state, action) => {
    //     state.dishProductsForModalArray = state.dishProductsForModalArray.map(product => {
    //         console.log(action.payload, product);
    //         if (product.productId === action.payload.productId) {
    //             return action.payload;
    //         }
    //         return product;
    //     });
    //     dishProductsSlice.caseReducers.recalculateTotals(state);
    // },
    removeDishProductForModal: (state, action) => {
      state.dishProductsForModalArray = state.dishProductsForModalArray.filter(
        (product) => product.id !== action.payload
      );
      dishProductsSlice.caseReducers.recalculateTotals(state);
    },
    clearArray: (state) => {
      state.dishProductsForModalArray = [];
      state.dishProductsWeight = 0;
      state.dishProductsWeightNetto = 0;
      state.dishProductsCost = 0;
      state.dishProductEnergy = 0;
      state.dishProductsFats = 0;
      state.dishProductsProteins = 0;
      state.dishProductsCarbohydrates = 0;
    },
    recalculateTotals: (state) => {
      const array = state.dishProductsForModalArray;

      state.dishProductsWeight = array.reduce(
        (sum, p) => sum + (parseFloat(p.weight) || 0),
        0
      );

      state.dishProductsWeightNetto = array.reduce((sum, product) => {
        const weight = parseFloat(product.weight) || 0;

        // Check if this is a manually added item with pre-calculated weightNetto
        if (product.weightNetto !== undefined) {
          return sum + (parseFloat(product.weightNetto) || 0);
        }

        // Handle API data structure - calculate weightNetto based on product's ratio
        if (product.product) {
          const prodWeight = parseFloat(product.product.weight) || 0;
          const prodWeightNetto = parseFloat(product.product.weightNetto) || 0;
          const nettoRatio = prodWeight > 0 ? prodWeightNetto / prodWeight : 0;
          return sum + weight * nettoRatio;
        }

        // Fallback - assume 100% ratio if no ratio data available
        return sum + weight;
      }, 0);

      state.dishProductsCost = parseFloat(
        array.reduce((sum, p) => sum + (parseFloat(p.cost) || 0), 0).toFixed(2)
      );
      state.dishProductEnergy = parseFloat(
        array
          .reduce((sum, p) => sum + (parseFloat(p.energy) || 0), 0)
          .toFixed(2)
      );
      state.dishProductsFats = parseFloat(
        array.reduce((sum, p) => sum + (parseFloat(p.fats) || 0), 0).toFixed(2)
      );
      state.dishProductsProteins = parseFloat(
        array
          .reduce((sum, p) => sum + (parseFloat(p.proteins) || 0), 0)
          .toFixed(2)
      );
      state.dishProductsCarbohydrates = parseFloat(
        array
          .reduce((sum, p) => sum + (parseFloat(p.carbohydrates) || 0), 0)
          .toFixed(2)
      );
    },
  },
});

const arrayFormattedDishProducts = createSelector(
  (state) => state.dishProducts.dishProductsForModalArray,
  (array) =>
    array.map((product, index) => ({
      index: index,
      productId: product.productId ?? product.id,
      weight: product.weight,
      weightNetto: product.weightNetto,
      cost: product.cost,
      price: product.price,
    }))
);

const { reducer: dishProductsReduces, actions } = dishProductsSlice;
const dishProductsAction = { ...actions };

export { dishProductsReduces, dishProductsAction, arrayFormattedDishProducts };
